import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChartLineUp, ShieldCheck, Buildings, Handshake, Sparkle, ArrowUpRight } from "@phosphor-icons/react";

const services = [
  { icon: ChartLineUp, title: "Wealth Management", desc: "Bespoke portfolios across equity, debt, gold and alternatives.", slug: "wealth-management" },
  { icon: Sparkle, title: "Mutual Fund Advisory", desc: "Direct plans, goal-mapped, reviewed every quarter.", slug: "mutual-fund-advisory" },
  { icon: ShieldCheck, title: "Financial Planning", desc: "Cashflow, insurance, retirement — a written plan.", slug: "financial-planning" },
  { icon: Buildings, title: "Corporate Advisory", desc: "Structuring, treasury and capital raise support.", slug: "corporate-advisory" },
  { icon: Handshake, title: "Trading & Brokerage", desc: "Execution, research and margin discipline.", slug: "trading-brokerage" },
  { icon: ChartLineUp, title: "Market Research", desc: "Independent, unbiased, thesis-driven notes.", slug: "market-research" },
];

const stats = [
  { k: "Est. 2022", v: "Boutique advisory" },
  { k: "₹50Cr+", v: "Assets under advisory" },
  { k: "200+", v: "Households onboarded" },
  { k: "4.9/5", v: "Client satisfaction" },
];

const testimonials = [
  { q: "Manigriv brought clarity to a decade of scattered investments. Our plan now has one voice.", by: "Anita R., New Delhi" },
  { q: "Their corporate structuring saved us both tax and headache. Rare combination.", by: "Rohit S., Founder, Gurugram" },
  { q: "The quarterly reviews are the most useful hour on my calendar.", by: "Karan M., Business Owner" },
];

export default function Home() {
  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="lg:col-span-7">
            <div className="overline text-gold-dark mb-6">Delhi · Since 2022</div>
            <h1 className="font-display text-5xl md:text-6xl leading-[1.05] tracking-tight text-midnight">
              A quieter kind of<br />
              <span className="italic gold-underline">wealth advice</span>.
            </h1>
            <p className="mt-8 text-lg text-midnight/70 max-w-xl leading-relaxed">
              Manigriv is a boutique advisory building tax-efficient portfolios for families and founders — grounded in research, insured by governance.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/book" data-testid="hero-book-btn" className="btn-gold">Book a Consultation</Link>
              <Link to="/calculators" data-testid="hero-calc-btn" className="btn-outline-ink">Try our Calculators</Link>
            </div>
            <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((s) => (
                <div key={s.k}>
                  <div className="font-display text-3xl text-midnight">{s.k}</div>
                  <div className="text-xs text-midnight/60 mt-1 leading-snug">{s.v}</div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15 }} className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(10,17,40,0.18)]">
              <img src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=80" alt="Financial cityscape" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-midnight/60 via-midnight/10 to-transparent"></div>
            </div>
            <div className="absolute -bottom-8 -left-8 w-56 bg-white rounded-xl p-5 shadow-[0_10px_40px_rgba(10,17,40,0.12)] border border-black/5 hidden md:block">
              <div className="overline text-gold-dark mb-2">SEBI Registered</div>
              <p className="text-sm text-midnight/80 leading-snug">Independent · Fee-only advisory model. No commissions in the room.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="bg-midnight text-white py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <div className="overline text-gold mb-4">What we do</div>
              <h2 className="font-display text-4xl md:text-5xl">Six practices,<br /><span className="italic text-gold">one philosophy</span>.</h2>
            </div>
            <p className="max-w-md text-white/70">Every mandate is written down and reviewed quarterly. No mystery, no hidden fees, no product pushing.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.map((s, i) => (
              <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`} className={`group relative rounded-2xl p-8 border border-white/10 hover:border-gold/50 transition-colors bg-white/[0.02] hover:bg-white/[0.04] ${i === 0 ? "md:col-span-2" : ""}`}>
                <s.icon size={32} weight="thin" className="text-gold mb-6" />
                <h3 className="font-display text-2xl mb-2">{s.title}</h3>
                <p className="text-sm text-white/60 mb-8 leading-relaxed">{s.desc}</p>
                <div className="flex items-center gap-2 text-gold text-sm font-medium">Learn more <ArrowUpRight size={16} /></div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 bg-bone">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="overline text-gold-dark mb-4">In their words</div>
          <h2 className="font-display text-4xl md:text-5xl text-midnight max-w-2xl mb-14">Trusted by families and founders across India.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 border border-black/5 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
                <div className="text-gold text-3xl font-display leading-none mb-4">“</div>
                <p className="text-midnight/80 leading-relaxed mb-6">{t.q}</p>
                <div className="text-xs text-midnight/60 font-medium">{t.by}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 bg-midnight rounded-3xl p-12 md:p-20 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="overline text-gold mb-4">Ready when you are</div>
            <h2 className="font-display text-3xl md:text-5xl text-white">Let's build a plan you'll actually follow.</h2>
          </div>
          <div className="flex flex-col items-start md:items-end gap-4">
            <p className="text-white/70 max-w-md md:text-right">A 30-minute discovery call, on us. Human, unhurried, and off-the-record.</p>
            <Link to="/book" data-testid="cta-book-btn" className="btn-gold">Book Free Consultation</Link>
          </div>
        </div>
      </section>
    </>
  );
}
